import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Wallet } from "lucide-react";
import { usePageMeta } from "../hooks/usePageMeta";
import { EASE, Reveal, fadeUp, stagger } from "../lib/motion";
import { Marquee } from "../components/Marquee";
import { PercorsoCard } from "../components/PercorsoCard";
import { CtaBanner } from "../components/CtaBanner";
import { PERCORSI, MANIFESTO } from "../data/content";
import { SITE } from "../config/site";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden pb-1 -mb-1">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const { scrollY } = useScroll();
  const imgY = useTransform(scrollY, [0, 700], [0, 90]);

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-16"
    >
      <div className="absolute -top-40 -right-40 w-[620px] h-[620px] rounded-full bg-azure-light blur-3xl pointer-events-none" />
      <div className="absolute top-1/3 -left-48 w-[420px] h-[420px] rounded-full bg-sky-100/50 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 w-full relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-10 items-center">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: EASE, delay: 0.1 }}
              className="inline-flex items-center gap-3 text-xs font-bold uppercase tracking-[0.28em] text-azure mb-7"
            >
              <span className="w-8 h-[2px] bg-azure rounded-full" />
              Centro Studi · Orientamento e Formazione
            </motion.p>

            <h1 className="text-4xl sm:text-5xl lg:text-[4.6rem] font-extrabold tracking-tight text-navy leading-[1.04]">
              <MaskedLine delay={0.2}>Dai forma</MaskedLine>
              <MaskedLine delay={0.32}>al tuo</MaskedLine>
              <MaskedLine delay={0.44} className="font-serif-editorial italic font-medium text-azure">
                domani.
              </MaskedLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE, delay: 0.65 }}
              className="mt-7 text-base md:text-lg text-slate-600 leading-relaxed max-w-xl"
            >
              Orientamento e percorsi di formazione pensati per aiutarti a
              costruire il tuo futuro.
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE, delay: 0.72 }}
              data-testid="hero-slogan"
              className="mt-5 font-serif-editorial text-lg md:text-xl text-azure"
            >
              «Il tuo percorso, il nostro supporto.»
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE, delay: 0.8 }}
              className="mt-10 flex flex-col sm:flex-row gap-4"
            >
              <Link
                to="/contatti"
                data-testid="hero-cta-primary"
                className="inline-flex items-center justify-center gap-2.5 rounded-full bg-azure px-8 py-4 text-sm font-bold text-white shadow-[0_18px_38px_-14px_rgba(36,85,138,0.65)] transition-all duration-300 hover:bg-[#1C446E] hover:-translate-y-0.5 group"
              >
                Richiedi informazioni
                <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/percorsi"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center justify-center rounded-full border-2 border-navy/15 px-8 py-4 text-sm font-bold text-navy transition-all duration-300 hover:border-navy hover:-translate-y-0.5"
              >
                Scopri i percorsi
              </Link>
            </motion.div>
          </div>

          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1.1, ease: EASE, delay: 0.5 }}
              className="relative"
            >
              <div className="absolute -top-10 -left-10 w-40 h-40 rounded-full border border-dashed border-azure-mid spin-slower pointer-events-none" />
              <motion.div
                style={{ y: imgY }}
                className="relative rounded-[2rem] overflow-hidden shadow-[0_50px_100px_-40px_rgba(8,42,89,0.5)] border-8 border-white"
              >
                <img
                  src="/assets/hero-student.jpg"
                  alt="Studente al lavoro in biblioteca"
                  className="w-full h-[380px] sm:h-[440px] lg:h-[520px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/35 via-transparent to-transparent" />
              </motion.div>
              <div className="float-slow absolute -bottom-7 -left-4 sm:-left-8 bg-white rounded-2xl shadow-[0_24px_50px_-20px_rgba(8,42,89,0.35)] border border-slate-100 px-5 py-4 flex items-center gap-3.5">
                <img src="/assets/favicon.png" alt="" className="w-9 h-9 object-contain" />
                <div>
                  <p className="text-[13px] font-bold text-navy leading-tight">
                    Orientamento personalizzato
                  </p>
                  <p className="text-[11.5px] text-slate-500">
                    Un percorso costruito su di te
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

const PercorsiSection = () => (
  <section data-testid="percorsi-section" className="py-20 lg:py-28">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
      <div className="max-w-2xl mb-14">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-azure mb-4">
            I nostri percorsi
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-navy text-balance">
            Quattro strade, un unico punto di partenza:{" "}
            <span className="font-serif-editorial italic font-medium text-azure">tu.</span>
          </h2>
          <span
            data-testid="percorsi-rateale-badge"
            className="mt-7 inline-flex items-center gap-2.5 rounded-full bg-azure-light border border-azure-soft px-5 py-2.5 text-sm font-semibold text-navy"
          >
            <Wallet size={16} className="text-azure shrink-0" />
            Possibilità di pagamento a rate su tutti i percorsi
          </span>
        </Reveal>
      </div>
      <motion.div
        variants={stagger}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-60px" }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
      >
        {PERCORSI.map((p, i) => (
          <PercorsoCard key={p.slug} percorso={p} index={i} />
        ))}
      </motion.div>
    </div>
  </section>
);

const ManifestoSection = () => (
  <section data-testid="manifesto-section" className="bg-navy text-white py-20 lg:py-28 relative overflow-hidden">
    <div className="grain absolute inset-0" />
    <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-azure-veil blur-3xl pointer-events-none" />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 relative">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-4">
          <Reveal className="lg:sticky lg:top-32">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-sky-300 mb-4">
              Perché Forma Futuro
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-balance">
              Non un semplice centro studi.
            </h2>
            <p className="mt-6 text-base text-white/60 leading-relaxed">
              Il nostro lavoro è accompagnarti: dall'orientamento iniziale fino
              al traguardo, con chiarezza e presenza costante.
            </p>
          </Reveal>
        </div>
        <div className="lg:col-span-8">
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-60px" }}
            className="divide-y divide-white/10"
          >
            {MANIFESTO.map((c) => (
              <motion.div
                key={c.num}
                variants={fadeUp}
                data-testid={`manifesto-chapter-${c.num}`}
                className="group grid grid-cols-1 sm:grid-cols-[90px_1fr] gap-4 sm:gap-8 py-9 first:pt-0 last:pb-0"
              >
                <span className="font-serif-editorial italic text-4xl sm:text-5xl text-sky-400/80 transition-colors duration-300 group-hover:text-sky-300">
                  {c.num}
                </span>
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold tracking-tight">
                    {c.titolo}
                  </h3>
                  <p className="mt-3 text-sm sm:text-base text-white/60 leading-relaxed max-w-xl">
                    {c.testo}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </div>
  </section>
);

const ChiSiamoTeaser = () => (
  <section data-testid="chi-siamo-teaser" className="py-20 lg:py-28">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
        <Reveal className="relative order-2 lg:order-1">
          <div className="rounded-[2rem] overflow-hidden border-8 border-white shadow-[0_40px_80px_-40px_rgba(8,42,89,0.45)]">
            <img
              src="/assets/chi-siamo.jpg"
              alt="Spazio di studio contemporaneo"
              className="w-full h-[340px] sm:h-[420px] object-cover"
            />
          </div>
          <div className="absolute -bottom-6 -right-3 sm:-right-6 bg-azure text-white rounded-2xl px-6 py-5 shadow-xl">
            <p className="font-serif-editorial italic text-lg leading-snug">
              "{SITE.tagline}"
            </p>
          </div>
        </Reveal>
        <div className="order-1 lg:order-2">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-azure mb-4">
              Chi siamo
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-navy text-balance">
              Una realtà dedicata all'orientamento e alla formazione.
            </h2>
            <p className="mt-6 text-base text-slate-600 leading-relaxed">
              Forma Futuro nasce con un obiettivo chiaro: aiutare ogni persona a
              trovare il percorso di studio o di crescita professionale più
              adatto alle proprie esigenze, seguendola con attenzione in ogni
              fase.
            </p>
            <Link
              to="/chi-siamo"
              data-testid="chi-siamo-teaser-link"
              className="mt-8 inline-flex items-center gap-2.5 text-sm font-bold text-azure group"
            >
              Scopri chi siamo
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

export default function Home() {
  usePageMeta(null);
  return (
    <main>
      <Hero />
      <Marquee />
      <PercorsiSection />
      <ManifestoSection />
      <ChiSiamoTeaser />
      <CtaBanner />
    </main>
  );
}
